import { useEffect, useState } from 'react'

import { useAsyncCallback, useAsyncTask } from '../../../../hooks'
import {
  EpostCell,
  getEpostUkCreditsAfter,
  isEpostLoaded,
  isEpostLoading,
  useEpostSupportModal,
} from '../../../Epost'
import {
  calculateDocFlowSendingEpostCost,
  DocFlowSendingWidgetProcessFieldName,
  getDocFlowSendingEpostSettings,
  isDocFlowSendingWidgetStatusCompleted,
  isDocFlowSendingWidgetTypeEpostSettings,
  updateDocFlowSendingEpostSettings,
} from '../../DocFlowSending'
import { useDocFlowContext } from '../useDocFlowContext'
import { useDocFlowEpostSettingsNotifications } from '../useDocFlowEpostSettingsNotifications'

import {
  USE_DOC_FLOW_EPOST_SETTINGS_ASYNC_ID,
  USE_DOC_FLOW_EPOST_SETTINGS_ASYNC_TIME_OUT,
} from './useDocFlowEpostSettings.constants'

export function useDocFlowEpostSettings({
  activeWidget,
  epostFormikRef,
  processData,
}) {
  const { documentId, phaseDisabled } = useDocFlowContext()

  const epostSupportModalData = useEpostSupportModal()

  const [epostSettings, setEpostSettings] = useState(null)
  const [isEpostSettingsLoading, setEpostSettingsLoading] = useState(false)
  const [isCalculating, setIsCalculating] = useState(false)
  const [isCalculated, setIsCalculated] = useState(false)

  const isActiveWidget = isDocFlowSendingWidgetTypeEpostSettings(activeWidget)

  const [prevIsActiveWidget, setPrevIsActiveWidget] = useState(isActiveWidget)

  // Reset during render when the widget becomes active; the matching load runs
  // in the effect below.
  if (isActiveWidget !== prevIsActiveWidget) {
    setPrevIsActiveWidget(isActiveWidget)

    if (isActiveWidget) {
      setEpostSettings(null)
    }
  }

  const noEpostSettings =
    processData &&
    processData[DocFlowSendingWidgetProcessFieldName.IsEpostDisabledForAll]

  const {
    handleAddNotification,
    handlePrependNotification,
    handleRemoveAdditionalNotifications,
    handleRemoveNotification,
    notifications,
    setInfoNotification,
    setNotEnoughCreditsNotification,
    setNotificationsByGettingSettings,
    widgetNotifications,
  } = useDocFlowEpostSettingsNotifications({
    isActiveWidget,
    noEpostSettings,
    onOpenSupportData: epostSupportModalData.handleOpenModal,
  })

  const widgetDone = isDocFlowSendingWidgetStatusCompleted(
    processData?.[DocFlowSendingWidgetProcessFieldName.EpostSettingsPhase]
  )

  const epostUkCreditsAfter = getEpostUkCreditsAfter(
    epostSettings?.[EpostCell.TotalCost],
    epostSettings?.[EpostCell.UkCredits]
  )

  const isEnoughCredits = epostUkCreditsAfter >= 0

  const calculatingData = epostSettings
    ? {
        isEnoughCredits,
        total: epostSettings?.[EpostCell.TotalCost],
        ukCredits: epostSettings?.[EpostCell.UkCredits],
        ukCreditsAfter: epostUkCreditsAfter,
      }
    : null

  const getEpostSettings = useAsyncCallback(() => {
    setEpostSettingsLoading(true)

    return getDocFlowSendingEpostSettings(documentId)
      .then(({ data }) => {
        setEpostSettings(data)

        const calculated = isEpostLoaded(data[EpostCell.Loading])

        const enoughCredits =
          getEpostUkCreditsAfter(
            data[EpostCell.TotalCost],
            data[EpostCell.UkCredits]
          ) >= 0

        if (data[EpostCell.SendToCalculateAt] && calculated) {
          setIsCalculated(true)
        } else if (!data[EpostCell.SendToCalculateAt]) {
          setIsCalculated(false)
        }

        if (data[EpostCell.SendToCalculateAt] && !calculated) {
          setIsCalculating(true)
        }

        setInfoNotification(data)

        if (calculated) {
          setNotificationsByGettingSettings(data, enoughCredits)

          if (!enoughCredits && data[EpostCell.ValidationErrors].length === 0) {
            setNotEnoughCreditsNotification(true)
          }
        }

        return data
      })
      .finally(() => {
        setEpostSettingsLoading(false)
      })
  }, [
    documentId,
    setInfoNotification,
    setNotEnoughCreditsNotification,
    setNotificationsByGettingSettings,
  ])

  const handleUpdateEpostSettings = useAsyncCallback(
    data => {
      setEpostSettingsLoading(true)
      setIsCalculating(true)

      if (
        notifications.find(
          item => item.id === widgetNotifications.notEnoughCreditsWithLink.id
        )
      ) {
        setNotEnoughCreditsNotification()
      }

      return updateDocFlowSendingEpostSettings({ data, documentId })
        .then(({ data }) => {
          setEpostSettings(data)

          return calculateDocFlowSendingEpostCost(documentId).then(
            ({ data: calculateData }) => {
              setEpostSettings(calculateData)

              return calculateData
            }
          )
        })
        .finally(() => {
          setEpostSettingsLoading(false)
        })
    },
    [
      documentId,
      notifications,
      setNotEnoughCreditsNotification,
      widgetNotifications,
    ]
  )

  const { asyncTasks, handleAddAsyncTask, handleRemoveAsyncTask } =
    useAsyncTask()

  useEffect(() => {
    if (phaseDisabled && widgetDone && isActiveWidget) {
      handleAddNotification(widgetNotifications.success)
    }
  }, [isActiveWidget])

  useEffect(() => {
    if (isActiveWidget) {
      getEpostSettings()
    }
  }, [isActiveWidget])

  useEffect(() => {
    if (
      isActiveWidget &&
      epostSettings?.[EpostCell.SendToCalculateAt] &&
      isEpostLoading(epostSettings?.[EpostCell.Loading]) &&
      !phaseDisabled
    ) {
      if (asyncTasks.length === 0) {
        handleAddAsyncTask({
          id: USE_DOC_FLOW_EPOST_SETTINGS_ASYNC_ID,
          interval: setInterval(() => {
            getEpostSettings()
          }, USE_DOC_FLOW_EPOST_SETTINGS_ASYNC_TIME_OUT),
        })
      }
    } else if (asyncTasks.length > 0) {
      handleRemoveAsyncTask(USE_DOC_FLOW_EPOST_SETTINGS_ASYNC_ID)
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reacts to async epost task completion
      setIsCalculating(false)
      epostFormikRef.current?.setSubmitting(false)
    }
  }, [epostSettings])

  return {
    calculatingData,
    epostNotificationsData: {
      handleAddNotification,
      handlePrependNotification,
      handleRemoveAdditionalNotifications,
      handleRemoveNotification,
      notifications,
      widgetNotifications,
    },
    epostSettings,
    epostSupportModalData,
    getEpostSettings,
    handleUpdateEpostSettings,
    isCalculated,
    isCalculating,
    isEpostSettingsLoading,
    noEpostSettings,
  }
}
